import { useCallback, useEffect, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import { isBrowser } from "^/utils/isBrowser";
import PlaceholderOrOpenStreetMapComponent from "../../Components/PlaceholderOrOpenStreetMap";

import { MarkerPopup } from "./MarkerPopup";
import {
  createIcon,
  getMarkerKeys,
  useMarkerCoordinates,
  useOrientation,
} from "./OpenStreetMapWidgetUtils";
import FitBounds from "./FitBounds";
import CtrlWheelZoom from "./CtrlWheelZoom";

const OpenStreetMapWidgetComponent = Scrivito.connect(({ widget }) => (
  <PlaceholderOrOpenStreetMapComponent widget={widget}>
    <OpenStreetMapContent widget={widget} />
  </PlaceholderOrOpenStreetMapComponent>
));

const HoverMarker = ({ coord, Marker, Popup }) => {
  const closeTimerRef = useRef(null);
  const markerRef = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      if (markerRef.current) markerRef.current.closePopup();
    }, 200);
  }, []);

  return (
    <Marker
      ref={markerRef}
      position={[coord.lat, coord.lon]}
      icon={createIcon(coord.color)}
      eventHandlers={{
        mouseover: () => {
          cancelClose();
          markerRef.current?.openPopup();
        },
        mouseout: scheduleClose,
        popupopen: (e) => {
          const container = e.popup.getElement();
          if (container) {
            container.addEventListener("mouseenter", cancelClose);
            container.addEventListener("mouseleave", scheduleClose);
          }
        },
        popupclose: (e) => {
          const container = e.popup.getElement();
          if (container) {
            container.removeEventListener("mouseenter", cancelClose);
            container.removeEventListener("mouseleave", scheduleClose);
          }
        },
      }}
    >
      <Popup>
        <MarkerPopup coord={coord} />
      </Popup>
    </Marker>
  );
};

const OpenStreetMapContent = Scrivito.connect(({ widget }) => {
  const markers = widget.get("markers") || [];
  const markerKeys = getMarkerKeys(markers);
  const coordinates = useMarkerCoordinates(markers, markerKeys);
  const [isPortrait, resizeId] = useOrientation();
  const zoom = 13;
  const [isScrivitoLoaded, setIsScrivitoLoaded] = useState(!isBrowser);

  useEffect(() => {
    if (!isBrowser) return;
    Scrivito.finishLoading().then(() => setIsScrivitoLoaded(true));
  }, []);

  // leaflet is not SSR-compatible, so we have to require it dynamically
  if (!isBrowser) return null;
  // eslint-disable-next-line global-require
  const { MapContainer, TileLayer, Marker, Popup } = require("react-leaflet");
  // eslint-disable-next-line global-require
  require("leaflet/dist/leaflet.css");

  const aspectRatio = isPortrait ? "9/16" : "16/9";

  return (
    <div className="openstreetmap-widget">
      <div style={{ width: "100%", aspectRatio }}>
        <MapContainer
          center={
            coordinates.length
              ? [coordinates[0].lat, coordinates[0].lon]
              : [49.1427, 9.2109]
          }
          zoom={zoom}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap contributors"
          />

          {coordinates.map((coord, i) => (
            <HoverMarker key={i} coord={coord} Marker={Marker} Popup={Popup} />
          ))}

          <CtrlWheelZoom />
          {isScrivitoLoaded && (
            <FitBounds
              coordinates={coordinates}
              zoom={zoom}
              resizeTrigger={resizeId}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
});

Scrivito.provideComponent("OpenStreetMapWidget", OpenStreetMapWidgetComponent);

export default OpenStreetMapWidgetComponent;
